import {
    AfterViewInit,
    Component,
    ElementRef,
    inject,
    Injectable,
    signal,
} from '@angular/core';

import {
    lastValueFrom
} from 'rxjs';

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from '@angular/material/dialog';

import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent
} from "@tilby/tilby-ui-lib/components/tilby-dialog";

import {
    CustomForm,
    CustomFormControl,
    CustomFormControlProps,
    CustomFormGroup,
    TilbyMagicFormComponent
} from '@tilby/tilby-ui-lib/components/tilby-magic-form';

import {
    Validators
} from '@angular/forms';

import {
    BarcodeManagerService,
    EnvironmentInfoService
} from 'src/app/core';

import {
    GeneralInputButton
} from '@tilby/tilby-ui-lib/models';

import {
    CommonModule
} from '@angular/common';

import {
    MatButtonModule
} from '@angular/material/button';

import {
    TranslateModule
} from '@ngx-translate/core';

type PromptDialogInput = {
    title?: string;
    titleParams?: { [key: string]: string };
    subtitle?: string;
    subtitleParams?: { [key: string]: string };
    message?: string;
    type?: 'text' | 'number' | 'email';
    label?: string;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    step?: number;
    value?: string;
    allowCamera?: boolean;
    disableCancel?: boolean;
    /** Prevents closing by clicking outside or pressing Esc, while keeping the cancel button */
    disableClose?: boolean;
    /** Shows a button inside the field that empties it (ignored when the camera button is shown) */
    clearable?: boolean;
    pattern?: string;
    /** Extra toolbar buttons. The dialog closes (without a value) unless the callback returns false. */
    actions?: { icon: string; callback: () => boolean | void | Promise<boolean | void> }[];
};

@Component({
    selector: 'app-prompt-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        TranslateModule,
        TilbyMagicFormComponent,
        TilbyDialogToolbarComponent,
        TilbyDialogContentComponent
    ],
    templateUrl: './prompt-dialog.component.html',
    styleUrls: ['./prompt-dialog.component.scss']
})
export class PromptDialog implements AfterViewInit {
    protected readonly data: PromptDialogInput = inject(MAT_DIALOG_DATA);
    private readonly barcodeManagerService = inject(BarcodeManagerService);
    private readonly environmentInfoService = inject(EnvironmentInfoService);
    private readonly el = inject(ElementRef);
    private readonly dialogRef = inject(MatDialogRef);

    protected form: CustomFormGroup<CustomForm<any>>

    protected readonly title = this.data.title || 'APPLICATION.PROMPT_DIALOG.TITLE_LABEL';

    // The toolbar replaces its default confirm button when custom actions are present, so we have to add it back
    protected readonly customActions: GeneralInputButton[] = this.data.actions?.length
        ? [
            ...this.data.actions.map(action => ({
                name: '',
                icon: signal(action.icon),
                isIt: () => true,
                click: async () => {
                    if(await action.callback() !== false) {
                        this.dialogRef.close();
                    }
                }
            })),
            {
                name: '',
                icon: signal('check'),
                isIt: () => !!this.form?.valid,
                click: () => this.confirm()
            }
        ]
        : [];

    ngAfterViewInit(): void {
        setTimeout(() => {
            const input = this.el.nativeElement.querySelector('#input-dialog-field input');

            if (input) {
                input.focus();
            }
        }, 200)
    }

    constructor() {
        this.form = new CustomFormGroup<CustomForm<any>>({
            inputValue: new CustomFormControl(
                this.data.value || undefined,
                {
                    validators: [
                        Validators.required,
                        ...(this.data.type !== 'number' ? [Validators.pattern(/\S/)] : []),
                        ...(this.data.type === 'email' ? [Validators.email] : []),
                        ...(this.data.minLength ? [Validators.minLength(this.data.minLength)] : []),
                        ...(this.data.maxLength ? [Validators.maxLength(this.data.maxLength)] : []),
                        ...(this.data.min ? [Validators.min(this.data.min)] : []),
                        ...(this.data.max ? [Validators.max(this.data.max)] : []),
                        ...(this.data.pattern ? [Validators.pattern(this.data.pattern)] : [])
                    ]
                }, {
                    ...new CustomFormControlProps(),
                    id: 'input-dialog-field',
                    label: this.data.label || 'APPLICATION.PROMPT_DIALOG.NAME_LABEL',
                    inputType: this.data.type || 'text',
                    ...(this.data.maxLength && { inputConstraint: { maxLength: this.data.maxLength } }),
                    hint: this.data.maxLength ? () => `${this.form.get('inputValue')?.value?.length || 0} / ${this.data.maxLength}` : undefined,
                    ...(this.data.allowCamera && this.environmentInfoService.isCameraBarcodeSupported()
                        ? {
                            customActions: {
                                suffix: {
                                    icon: "photo_camera",
                                    callback: () => this.openCameraBarcode()
                                }
                            }
                        }
                        : this.data.clearable && {
                            customActions: {
                                suffix: {
                                    icon: 'close',
                                    callback: () => this.form.get('inputValue')?.reset()
                                }
                            }
                        })
                }
            )
        });
    }

    confirm() {
        if(!this.form.valid) {
            return;
        }

        this.dialogRef?.close({
            confirmed: true,
            value: this.form.get('inputValue')?.value
        });
    }

    private async openCameraBarcode() {
        const newValue = await this.barcodeManagerService.openCameraBarcode();

        this.form?.patchValue({ inputValue: newValue });
    }
}

@Injectable({
    providedIn: 'root',
})
export class PromptDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public async openDialog(config: NonNullableConfigData<PromptDialogInput>): Promise<any> {
        const dialogConfig: NonNullableConfigData<PromptDialogInput> = {
            ...this.switchMobileDesktopDimensions({ width: '800px' }),
            ...config,
            ...{ disableClose: !!config.data.disableCancel || !!config.data.disableClose }
        };

        return lastValueFrom(this.dialogRef.open(PromptDialog, dialogConfig).afterClosed()).then(res => {
            if(!res?.confirmed) {
                return null;
            }

            return res.value;
        });
    }
}
